import React from 'react';
import { GraduationCapIcon, CheckIcon, LockIcon } from 'lucide-react';
import { areaStudyOptions, FilterOption } from '../data/filterOptions';
import { TrackingNoticeLinks } from './TrackingNoticeLinks';

// Visual shell for the /request-information/v3 template: the marketing
// copy stays pinned on the left while every question is asked inside a
// single persistent card on the right.

export interface SplitCardFormValues {
  areaOfStudy: number;
  program: number;
  highestEducation: string;
  hsGraduationYear: string;
  startDate: string;
  streetAddress: string;
  city: string;
  state: string;
  zipCode: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  consent: boolean;
}
export interface RequestInfoSplitCardProps {
  badge: string;
  headline: string;
  headlineAccent: string;
  subhead: string;
  features: string[];
  // Flow position. `step` matches the shared flow (0 = program selection,
  // 1 = education, 2 = grad year, 3 = timeframe, 5 = address, 6 = contact).
  // `stage` splits step 0 into area (0) then program (1).
  step: number;
  stage: number;
  formData: SplitCardFormValues;
  availablePrograms: FilterOption[];
  years: number[];
  error: string;
  tcpaLoading: boolean;
  tcpaError: string;
  tcpaDisclaimerHtml: string | null;
  /** True while the address step's ZIP is being verified. */
  addressChecking?: boolean;
  onFieldChange: (field: keyof SplitCardFormValues, value: string | number | boolean) => void;
  onAreaSelect: (area: number) => void;
  onZipChange: (zip: string) => void;
  onPhoneChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onContinue: () => void;
  onBack: () => void;
}

// Visual Arts & Graphic Design (12) isn't offered on this template.
const HIDDEN_AREA_VALUES = [12];
const AREA_TILES = areaStudyOptions.filter(option => !HIDDEN_AREA_VALUES.includes(option.value));
const STATES = ['AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'DC', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'];
const START_OPTIONS: {
  value: string;
  label: string;
}[] = [{
  value: 'immediately',
  label: 'Immediately'
}, {
  value: '1-3-months',
  label: 'Within 1–3 months'
}, {
  value: '3-6-months',
  label: 'Within 3–6 months'
}, {
  value: '6-12-months',
  label: 'Within 6–12 months'
}, {
  value: '1-year-plus',
  label: 'More than a year out'
}, {
  value: 'undecided',
  label: 'Undecided'
}];
const EDUCATION_OPTIONS: {
  value: string;
  label: string;
}[] = [{
  value: 'high-school',
  label: 'High School Diploma/GED'
}, {
  value: 'some-college',
  label: 'Some College'
}, {
  value: 'associates',
  label: "Associate's Degree"
}, {
  value: 'bachelors',
  label: "Bachelor's Degree"
}, {
  value: 'some-high-school',
  label: 'Some High School'
}];
const FALLBACK_CONSENT = 'By checking this box and clicking "See My Matches," I provide my electronic signature and express written consent for findonlinedegrees.org and up to four (4) partner schools to contact me at the phone number and email provided above — including by SMS/text messages and calls using an automatic telephone dialing system or an artificial/prerecorded voice — regarding educational opportunities. Message and data rates may apply. Message frequency varies. Consent is not a condition of enrollment or purchase. I can revoke consent at any time.';
const CARD_COPY: Record<string, {
  title: string;
  subtitle: string;
}> = {
  area: {
    title: "Let's Get Started",
    subtitle: 'Select your area of interest'
  },
  program: {
    title: 'Narrow it down',
    subtitle: 'Pick the program closest to your interest'
  },
  education: {
    title: 'Your background',
    subtitle: 'This helps us match you to programs you qualify for'
  },
  gradYear: {
    title: 'Almost there',
    subtitle: 'What year did you graduate high school?'
  },
  timing: {
    title: 'Timing',
    subtitle: 'When would you like to start classes?'
  },
  address: {
    title: 'Where are you located?',
    subtitle: 'Used to match you with programs available in your state'
  },
  contact: {
    title: 'Almost done',
    subtitle: 'Where should schools reach you?'
  }
};
const selectClasses = (filled: boolean) => `w-full px-4 py-3 bg-white border rounded-xl text-gray-900 appearance-none cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-[#e8594f]/30 ${filled ? 'border-[#e8594f]' : 'border-gray-200 hover:border-gray-300'}`;
const inputClasses = 'w-full px-4 py-3 bg-white border border-gray-200 rounded-xl text-gray-900 transition-colors placeholder:text-gray-400 focus:outline-none focus:border-[#e8594f] focus:ring-2 focus:ring-[#e8594f]/30 hover:border-gray-300';
const chevronStyle = {
  backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`,
  backgroundPosition: 'right 0.75rem center',
  backgroundRepeat: 'no-repeat',
  backgroundSize: '1.25em 1.25em',
  paddingRight: '2.5rem'
};
const FieldLabel = ({
  children
}: {
  children: React.ReactNode;
}) => <span className="block text-sm font-semibold text-gray-900 mb-2" data-id="element-499" data-mp="3k4z9ufm6i9fl">
    {children} <span className="text-[#e8594f]" data-id="element-500" data-mp="3f6xrxy0hog5p">*</span>
  </span>;
export function RequestInfoSplitCard({
  badge,
  headline,
  headlineAccent,
  subhead,
  features,
  step,
  stage,
  formData,
  availablePrograms,
  years,
  error,
  tcpaLoading,
  tcpaError,
  tcpaDisclaimerHtml,
  addressChecking = false,
  onFieldChange,
  onAreaSelect,
  onZipChange,
  onPhoneChange,
  onContinue,
  onBack
}: RequestInfoSplitCardProps) {
  const screen = step === 0 ? stage === 0 ? 'area' : 'program' : step === 1 ? 'education' : step === 2 ? 'gradYear' : step === 3 ? 'timing' : step === 5 ? 'address' : 'contact';
  const displayStep = screen === 'area' ? 1 : screen === 'program' ? 2 : screen === 'education' ? 3 : screen === 'gradYear' ? 4 : screen === 'timing' ? 5 : screen === 'address' ? 6 : 7;
  const canGoBack = !(step === 0 && stage === 0);
  const isFinalStep = screen === 'contact';
  const copy = CARD_COPY[screen];
  return <div className="min-h-screen bg-[#f4f5f7] flex flex-col" data-id="element-501" data-mp="2exfyg7dvzmzf">
      {/* Advertising disclosure */}
      <div className="bg-gray-100 border-b border-gray-200 py-2.5 px-4" data-id="element-502" data-mp="2jw99zu9m53wy">
        <p className="text-center text-xs text-gray-600" data-id="element-503" data-mp="37opra2lj0rv7">
          We earn commissions from schools listed on this site, which influences
          how listings are presented.{' '}
          <a href="/terms" className="underline hover:text-gray-900" data-id="element-504" data-mp="2ymgm7s911qp7">
            Advertising Disclosure
          </a>
        </p>
      </div>

      {/* Brand bar */}
      <header className="bg-white border-b border-gray-200 py-4 px-4" data-id="element-505" data-mp="1n8l368f04xsp">
        <a href="/" className="flex items-center justify-center gap-2.5" data-id="element-506" data-mp="1nafnplmd2nw9">
          <span className="w-9 h-9 rounded-lg bg-[#e8594f] flex items-center justify-center" data-id="element-507" data-mp="1rbhq2bbkrg2g">
            <GraduationCapIcon className="w-5 h-5 text-white" aria-hidden="true" data-id="element-508" data-mp="1joueb9uen85y" />
          </span>
          <span className="text-xl font-bold text-gray-900 tracking-tight" data-id="element-509" data-mp="1eqswstcmngwl">
            FindOnlineDegrees
          </span>
        </a>
      </header>

      <main className="flex-1 px-4 py-8 md:py-16" data-id="element-510" data-mp="1opruxhzrnu5c">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-8 lg:gap-16 items-center" data-id="element-511" data-mp="1oq1lb6oq43bk">
          {/* Marketing column — below the card on mobile */}
          <div className="order-2 lg:order-1" data-id="element-512" data-mp="w1cv42p7hiel">
            <div className="text-center lg:text-left" data-id="element-513" data-mp="w2ny40atum4n">
              <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-3.5 py-1.5 rounded-full text-sm font-semibold mb-6" data-id="element-514" data-mp="1aq91zwfj0pdm">
                <CheckIcon className="w-3.5 h-3.5" aria-hidden="true" data-id="element-515" data-mp="15xplorpgfg54" />
                {badge}
              </div>
            </div>
            <h1 className="text-center lg:text-left text-4xl sm:text-5xl font-extrabold text-gray-900 leading-[1.05] tracking-tight mb-6" data-id="element-516" data-mp="dacyqgfdt6ba">
              {headline}
              <span className="block text-[#e8594f]" data-id="element-517" data-mp="3ny90rv33h805">{headlineAccent}</span>
            </h1>
            <p className="text-base md:text-lg text-gray-600 leading-relaxed mb-7 max-w-md" data-id="element-518" data-mp="3sw119dht22fy">
              {subhead}
            </p>
            <ul className="space-y-3" data-id="element-519" data-mp="1rkfaf1dxzks">
              {features.map(feature => <li key={feature} className="flex items-center gap-3 text-gray-800" data-id="element-520" data-mp="5ooywk7xo27m">
                  <CheckIcon className="w-4 h-4 text-green-500 flex-shrink-0" aria-hidden="true" data-id="element-521" data-mp="apmf4r9dum6h" />
                  <span data-id="element-522" data-mp="fn4qspesbfi6">{feature}</span>
                </li>)}
            </ul>
          </div>

          {/* Question card — first on mobile so it lands above the fold */}
          <div className="order-1 lg:order-2 bg-white rounded-2xl shadow-[0_10px_40px_-12px_rgba(15,23,42,0.18)] p-6 sm:p-8" data-id="element-523" data-mp="pdhaygiot6ns">
            <div className="text-center mb-6" data-id="element-524" data-mp="teay37ob2hfm">
              <p className="font-mono text-xs tracking-[0.18em] text-[#e8594f] mb-2" data-id="element-525" data-mp="yep7ou5k1zfr">
                STEP {displayStep} OF 7
              </p>
              <h2 className="text-2xl font-bold text-gray-900 mb-1" data-id="element-526" data-mp="2ivet6o9fvqfd">
                {copy.title}
              </h2>
              <p className="text-sm text-gray-500" data-id="element-527" data-mp="2e0rbb88xxu9r">{copy.subtitle}</p>
            </div>

            {screen === 'area' && <div className="grid grid-cols-2 sm:grid-cols-3 gap-3" role="group" aria-label="Area of interest" data-id="element-528" data-mp="24y9puw1415nt">
                {AREA_TILES.map(option => {
              const selected = formData.areaOfStudy === option.value;
              return <button key={option.value} type="button" onClick={() => onAreaSelect(option.value)} aria-pressed={selected} className={`text-center px-3 py-4 rounded-xl border transition-all ${selected ? 'border-[#e8594f] bg-[#e8594f]/5 shadow-sm' : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'}`} data-id="element-529" data-mp="2xorrrtq3l0zn">
                      <span className={`mx-auto mb-2 flex w-4 h-4 items-center justify-center rounded border ${selected ? 'border-[#e8594f] bg-[#e8594f]' : 'border-gray-300 bg-white'}`} aria-hidden="true" data-id="element-530" data-mp="2ogi8efscuwe6">
                        {selected && <CheckIcon className="w-3 h-3 text-white" strokeWidth={3} data-id="element-531" data-mp="3cb2t74y99hc6" />}
                      </span>
                      <span className="block text-sm font-semibold text-gray-900 leading-snug" data-id="element-532" data-mp="3m38uj5aedb41">
                        {option.label}
                      </span>
                    </button>;
            })}
              </div>}

            {screen === 'program' && <label className="block" data-id="element-533" data-mp="3q4u70pj0rndp">
                <FieldLabel data-id="element-534" data-mp="3q5vjmyfoohxj">Program of Interest</FieldLabel>
                <select value={formData.program || ''} onChange={e => onFieldChange('program', Number(e.target.value))} className={selectClasses(Boolean(formData.program))} style={chevronStyle} data-id="element-535" data-mp="dlhmhtbbvwmb">
                  <option value="" data-id="element-536" data-mp="4mwayi49xhv1">Select a program</option>
                  {availablePrograms.map(option => <option key={option.value} value={option.value} data-id="element-537" data-mp="3r0e97vxdlkcx">
                      {option.label}
                    </option>)}
                </select>
              </label>}

            {screen === 'education' && <label className="block" data-id="element-538" data-mp="3h3ror88yovbf">
                <FieldLabel data-id="element-539" data-mp="3m0rufi1wfw20">Highest Education Level</FieldLabel>
                <select value={formData.highestEducation} onChange={e => onFieldChange('highestEducation', e.target.value)} className={selectClasses(Boolean(formData.highestEducation))} style={chevronStyle} data-id="element-540" data-mp="3lyyns2radbu0">
                  <option value="" data-id="element-541" data-mp="1im7qykewsj7v">Select one</option>
                  {EDUCATION_OPTIONS.map(option => <option key={option.value} value={option.value} data-id="element-542" data-mp="1drlcu9hg0ck3">
                      {option.label}
                    </option>)}
                </select>
              </label>}

            {screen === 'gradYear' && <label className="block" data-id="element-543" data-mp="21hpi0laa4kd7">
                <FieldLabel data-id="element-544" data-mp="21gmt2iwiorzx">HS Graduation Year</FieldLabel>
                <select value={formData.hsGraduationYear} onChange={e => onFieldChange('hsGraduationYear', e.target.value)} className={selectClasses(Boolean(formData.hsGraduationYear))} style={chevronStyle} data-id="element-545" data-mp="1wl7grve5r45m">
                  <option value="" data-id="element-546" data-mp="h3x6spunoagw">Select year</option>
                  {years.map(year => <option key={year} value={year} data-id="element-547" data-mp="c39jof9jsdry">
                      {year}
                    </option>)}
                  <option value="1989" data-id="element-548" data-mp="2npbfkpbmsxm7">1989 or earlier</option>
                </select>
              </label>}

            {screen === 'timing' && <label className="block" data-id="element-549" data-mp="2wr1dme00qusy">
                <FieldLabel data-id="element-550" data-mp="2wq9jz8pzf75b">Class Start Timeframe</FieldLabel>
                <select value={formData.startDate} onChange={e => onFieldChange('startDate', e.target.value)} className={selectClasses(Boolean(formData.startDate))} style={chevronStyle} data-id="element-551" data-mp="31jm8zjykpfyl">
                  <option value="" data-id="element-552" data-mp="3gftpoaarvz94">Select timeframe</option>
                  {START_OPTIONS.map(option => <option key={option.value} value={option.value} data-id="element-553" data-mp="3kgnf583v5m44">
                      {option.label}
                    </option>)}
                </select>
              </label>}

            {screen === 'address' && <div className="space-y-4" data-id="element-554" data-mp="uth8gkfsdlia">
                <label className="block" data-id="element-555" data-mp="usfy6i6lh31m">
                  <FieldLabel data-id="element-556" data-mp="uvbuy36phloh">Street Address</FieldLabel>
                  <input type="text" value={formData.streetAddress} onChange={e => onFieldChange('streetAddress', e.target.value)} placeholder="123 Main St" autoComplete="street-address" className={inputClasses} data-id="element-557" data-mp="qquf2f3gvsiz" />
                </label>
                <div className="grid grid-cols-3 gap-3" data-id="element-558" data-mp="74er7dp2duob">
                  <label className="block" data-id="element-559" data-mp="71tq4qwlbirm">
                    <FieldLabel data-id="element-560" data-mp="lt2k1c7izqak">City</FieldLabel>
                    <input type="text" value={formData.city} onChange={e => onFieldChange('city', e.target.value)} autoComplete="address-level2" className={inputClasses} data-id="element-561" data-mp="luvsmx1p8kl7" />
                  </label>
                  <label className="block" data-id="element-562" data-mp="2010ml0fgqm25">
                    <FieldLabel data-id="element-563" data-mp="1zzz0m0wwsf5n">State</FieldLabel>
                    <select value={formData.state} onChange={e => onFieldChange('state', e.target.value)} className={selectClasses(Boolean(formData.state))} style={chevronStyle} data-id="element-564" data-mp="24rh1koyxmfqm">
                      <option value="" data-id="element-565" data-mp="2ir8kn44b2mbh">State</option>
                      {STATES.map(abbr => <option key={abbr} value={abbr} data-id="element-566" data-mp="2nqcr3mzui5y4">
                          {abbr}
                        </option>)}
                    </select>
                  </label>
                  <label className="block" data-id="element-567" data-mp="2epmy0r1yyto3">
                    <FieldLabel data-id="element-568" data-mp="2eobw6ws33vzm">ZIP Code</FieldLabel>
                    <input type="text" inputMode="numeric" maxLength={5} value={formData.zipCode} onChange={e => onZipChange(e.target.value.replace(/\D/g, '').slice(0, 5))} autoComplete="postal-code" className={inputClasses} data-id="element-569" data-mp="ybeqa2luyowi" />
                  </label>
                </div>
              </div>}

            {screen === 'contact' && <div className="space-y-4" data-id="element-570" data-mp="2r8sb0if9ghs6">
                <div className="grid grid-cols-2 gap-3" data-id="element-571" data-mp="2r80i5fd1sy5l">
                  <label className="block" data-id="element-572" data-mp="2m9fsuazc3yvx">
                    <FieldLabel data-id="element-573" data-mp="2mah5gjw00tfr">First Name</FieldLabel>
                    <input type="text" value={formData.firstName} onChange={e => onFieldChange('firstName', e.target.value)} autoComplete="given-name" className={inputClasses} data-id="element-574" data-mp="2h9te1x3uyal0" />
                  </label>
                  <label className="block" data-id="element-575" data-mp="aj3gg6oc81eg">
                    <FieldLabel data-id="element-576" data-mp="6ib4v6rztkf0">Last Name</FieldLabel>
                    <input type="text" value={formData.lastName} onChange={e => onFieldChange('lastName', e.target.value)} autoComplete="family-name" className={inputClasses} data-id="element-577" data-mp="6it8wpkwd2xh" />
                  </label>
                </div>
                <div className="grid grid-cols-2 gap-3" data-id="element-578" data-mp="227kwjhzxlszt">
                  <label className="block" data-id="element-579" data-mp="2295qb5ef97wm">
                    <FieldLabel data-id="element-580" data-mp="15hkbnu2onwns">Email Address</FieldLabel>
                    <input type="email" value={formData.email} onChange={e => onFieldChange('email', e.target.value)} autoComplete="email" className={inputClasses} data-id="element-581" data-mp="15j56zlwtnjmp" />
                  </label>
                  <label className="block" data-id="element-582" data-mp="2pww0t9ghq5ip">
                    <FieldLabel data-id="element-583" data-mp="2pw45lzqt29sy">Phone Number</FieldLabel>
                    <input type="tel" value={formData.phone} onChange={onPhoneChange} placeholder="(555) 555-5555" autoComplete="tel" className={inputClasses} data-id="element-584" data-mp="2uvgueq18lqy6" />
                  </label>
                </div>

                <div className="bg-gray-50 border border-gray-200 rounded-xl p-4" data-id="element-585" data-mp="2fcrwt5ttj08h">
                  {tcpaLoading ? <div className="flex items-center gap-3 text-sm text-gray-600" data-id="element-586" data-mp="2p9o4587zdgbg">
                      <span className="w-4 h-4 border-2 border-gray-200 border-t-[#e8594f] rounded-full animate-spin" aria-hidden="true" data-id="element-587" data-mp="2pbr3iiv0wlsu" />
                      Loading consent terms…
                    </div> : tcpaError ? <p className="text-sm text-red-600" data-id="element-588" data-mp="2u8hif2v3dbco">{tcpaError}</p> : <label className="flex items-start gap-3 cursor-pointer" data-id="element-589" data-mp="13vs5acfaopfi">
                      <input type="checkbox" checked={formData.consent} onChange={e => onFieldChange('consent', e.target.checked)} className="mt-0.5 w-4 h-4 flex-shrink-0 rounded border-gray-300 text-[#e8594f] focus:ring-[#e8594f]" data-id="element-590" data-mp="zuq4hr5qc5bf" />
                      {tcpaDisclaimerHtml ? <span className="text-xs text-gray-600 leading-relaxed" dangerouslySetInnerHTML={{
                  __html: tcpaDisclaimerHtml
                }} data-id="element-591" data-mp="kzt8h5x1oohk" /> : <span className="text-xs text-gray-600 leading-relaxed" data-id="element-592" data-mp="b2ojkybteex2">
                          {FALLBACK_CONSENT} I also agree to the{' '}
                          <a href="/privacy-policy" className="underline hover:text-gray-900" data-id="element-593" data-mp="2173vh2bkf8w">
                            Privacy Policy
                          </a>{' '}
                          and{' '}
                          <a href="/terms" className="underline hover:text-gray-900" data-id="element-594" data-mp="1weygmpfy3f8k">
                            Terms of Use
                          </a>
                          .
                        </span>}
                    </label>}
                </div>
              </div>}

            {error && <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3" data-id="element-595" data-mp="2pz5dt45v4ovd">
                <p className="text-sm text-red-600" data-id="element-596" data-mp="2v0usquw7hmoq">{error}</p>
              </div>}

            <div className={`mt-6 ${canGoBack ? 'flex gap-3' : ''}`} data-id="element-597" data-mp="3iqf9d74w07uu">
              {canGoBack && <button type="button" onClick={onBack} className="px-6 py-3.5 rounded-xl border border-gray-200 bg-white font-semibold text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-300" data-id="element-598" data-mp="3no7dczit3kfe">
                  ← Back
                </button>}
              <button type="button" onClick={onContinue} disabled={addressChecking} className="flex-1 w-full px-6 py-3.5 rounded-xl bg-[#e8594f] font-bold text-white transition-colors hover:bg-[#d64a41] disabled:bg-[#e8594f]/60 disabled:cursor-wait focus:outline-none focus:ring-2 focus:ring-[#e8594f]/40" data-id="element-599" data-mp="22cp2jsmdv5hi">
                {addressChecking ? 'Checking your address…' : isFinalStep ? 'See My Matches →' : 'Continue →'}
              </button>
            </div>

            {isFinalStep && <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-gray-500" data-id="element-600" data-mp="170pjxxxcz1lv">
                <LockIcon className="w-3 h-3" aria-hidden="true" data-id="element-601" data-mp="1lt9lwv8rv31q" />
                Your information is secure and confidential
              </p>}
          </div>
        </div>
      </main>

      <footer className="py-8 px-4" data-id="element-602" data-mp="t8swi76mxfvt">
        <p className="text-center font-mono text-xs text-gray-500" data-id="element-603" data-mp="ocudz07zms3b">
          findonlinedegrees.org &nbsp;·&nbsp;
          <a href="/privacy-policy" className="underline hover:text-gray-700" data-id="element-604" data-mp="1dz59clmoike2">
            Privacy Policy
          </a>{' '}
          &nbsp;·&nbsp;
          <a href="/terms" className="underline hover:text-gray-700" data-id="element-605" data-mp="1hzfclqj36wp8">
            Terms of Use
          </a>{' '}
          &nbsp;·&nbsp;
          <a href="/cookie-policy" className="underline hover:text-gray-700" data-id="element-606" data-mp="1rruvqr64drbp">
            Cookie Policy
          </a>{' '}
          &nbsp;·&nbsp;
          <a href="/ca-notice" className="underline hover:text-gray-700" data-id="element-607" data-mp="v3f4mqnd39wh">
            Do Not Sell My Info
          </a>
        </p>
        <p className="mt-3 text-center text-xs text-gray-500 max-w-2xl mx-auto" data-id="element-608" data-mp="142a7ptv403vk">
          This page uses cookies and pixels from third parties, including Meta,
          for advertising and measurement. See our{' '}
          <a href="/cookie-policy" className="underline hover:text-gray-700" data-id="element-609" data-mp="2fhyvgng6t6xf">
            Cookie Policy
          </a>{' '}
          for opt-out options.
        </p>
      </footer>
    </div>;
}